import { useState, useEffect } from 'react';
import { Download, X, Wifi, WifiOff } from 'lucide-react';

// PWA Install Prompt Component
export const InstallPrompt = () => {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      // Show banner after a delay
      setTimeout(() => setShowBanner(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handler);
    
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setShowBanner(false);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setShowBanner(false);
    }
    setInstallPrompt(null);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    // Don't show again for this session
    sessionStorage.setItem('pwa-dismissed', 'true');
  };

  // Don't show if dismissed this session
  if (sessionStorage.getItem('pwa-dismissed')) return null;
  if (!showBanner || !installPrompt) return null;

  return (
    <div className="install-banner">
      <div className="flex items-center space-x-3">
        <Download className="w-6 h-6" />
        <div>
          <p className="font-medium text-sm">Install KPM Infra</p>
          <p className="text-xs opacity-80">Add to home screen for quick access</p>
        </div>
      </div>
      <div className="flex items-center space-x-2">
        <button onClick={handleInstall}>
          Install
        </button>
        <button 
          onClick={handleDismiss}
          className="p-2 hover:bg-white/10 rounded-full"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

// Offline Indicator Component
export const OfflineIndicator = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (isOnline) return null;

  return (
    <div className="offline-indicator flex items-center justify-center space-x-2">
      <WifiOff className="w-4 h-4" />
      <span>You're offline. Some features may be unavailable.</span>
    </div>
  );
};

// Hook to detect if app is installed as PWA
export const useIsPWA = () => {
  const [isPWA, setIsPWA] = useState(false);

  useEffect(() => {
    setIsPWA(
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true
    );
  }, []);

  return isPWA;
};

export default { InstallPrompt, OfflineIndicator, useIsPWA };
